import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import EvidenceBadge from "@/components/EvidenceBadge";
import PropertyAutocomplete from "@/components/PropertyAutocomplete";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { NeedsContactCard } from "@/components/NeedsContactCard";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { AlertTriangle, TrendingUp, MapPin, Database, Home, Users, FileText, CalendarClock, Clock, Search, Radio } from "lucide-react";

const KPI = ({ label, value, icon: Icon, to, testid }) => {
  const inner = (
    <Card className="p-5 border-slate-200 hover:border-slate-900 transition-colors cursor-pointer h-full" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-3xl font-bold font-mono text-slate-900">{value ?? 0}</div>
    </Card>
  );
  return <Link to={to}>{inner}</Link>;
};

export default function Dashboard() {
  const [k, setK] = useState({});
  const [upcoming, setUpcoming] = useState([]);
  const [stale, setStale] = useState([]);
  const [demand, setDemand] = useState(null);
  const [newAddr, setNewAddr] = useState("");
  const [newVerified, setNewVerified] = useState(null);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const createFromAddress = async () => {
    setCreating(true);
    try {
      const r = await api.post("/admin/properties/from-address", { address_id: newVerified.id });
      toast.success("Property created");
      navigate(`/properties/${r.data.property_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create property"); } finally { setCreating(false); }
  };

  useEffect(() => {
    api.get("/dashboard/kpis").then(r => setK(r.data));
    api.get("/funding/upcoming?days=90").then(r => setUpcoming(r.data)).catch(() => {});
    api.get("/alerts/stale-evidence?days=90").then(r => setStale(r.data)).catch(() => {});
    api.get("/admin/demand-density?weeks=8").then(r => setDemand(r.data)).catch(() => {});
  }, []);

  const evBreak = k.evidence_breakdown || {};
  const byV = k.programs_by_vertical || {};
  const byC = k.properties_by_county || {};

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Command Dashboard</div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <h1 className="text-3xl font-bold text-slate-900">Housing Opportunity Overview</h1>
          <div className="text-xs text-slate-500 font-mono">{new Date().toLocaleDateString("en-US", { weekday: 'long', month: 'short', day: 'numeric' })}</div>
        </div>
      </div>

      {isAdmin && <NeedsContactCard />}

      <Card className="p-5 border-slate-200 bg-slate-900 text-white" data-testid="quick-jump-card">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-300 font-semibold mb-2">
          <Search className="w-3.5 h-3.5" /> Jump to a property
        </div>
        <div className="text-white">
          <PropertyAutocomplete
            testid="dashboard-address-search"
            placeholder="Type an address — e.g., 3614 Versailles Dr"
            onSelect={(p) => navigate(`/properties/${p.id}`)}
            onCreateNew={(q) => setNewAddr(q)}
          />
        </div>
        <div className="text-[11px] text-slate-400 mt-2">Search across {k.properties || 0} vetted properties. Press Enter with no match to start a new one below.</div>
        <div className="mt-4 pt-4 border-t border-slate-700 text-slate-900" data-testid="dashboard-create-property">
          <div className="text-[10px] uppercase tracking-widest text-slate-300 font-semibold mb-2">Create a new property (Census-verified)</div>
          <div className="bg-white rounded-md p-3">
            <AddressAutocomplete id="dash-new-addr" label="Property address" value={newAddr} onChange={setNewAddr} lang="en" testId="dashboard-new-address" required={false}
              onVerified={setNewVerified} />
            <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[11px] text-slate-500">Suggestions and Street View share the public daily caps.</div>
              <button disabled={!(newVerified && newVerified.address_verified) || creating} onClick={createFromAddress}
                className="bg-slate-900 disabled:opacity-40 text-white text-sm font-semibold rounded-md px-4 py-2 min-h-[40px]" data-testid="dashboard-create-property-btn">
                {creating ? "Creating…" : "Create property"}
              </button>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label="Programs Tracked" value={k.programs} icon={Database} to="/programs" testid="kpi-programs" />
        <KPI label="Vetted Properties" value={k.properties} icon={Home} to="/properties" testid="kpi-properties" />
        <KPI label="Agencies & Partners" value={k.agencies} icon={Users} to="/agencies" testid="kpi-agencies" />
        <KPI label="Information Packets" value={k.packets} icon={FileText} to="/packets" testid="kpi-packets" />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Card className="p-5 border-slate-200" data-testid="upcoming-deadlines-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-slate-900 font-semibold"><CalendarClock className="w-4 h-4 text-[#C25E00]" /> Upcoming Funding Deadlines</div>
            <Link to="/funding" className="text-xs text-slate-500 hover:text-slate-900">View all →</Link>
          </div>
          <div className="space-y-1">
            {upcoming.slice(0, 5).map(f => {
              const urg = f.days_until <= 14 ? "text-rose-700" : f.days_until <= 45 ? "text-amber-700" : "text-emerald-700";
              return (
                <Link key={f.id} to="/funding" className="flex justify-between items-start gap-3 py-2 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 rounded px-1 -mx-1">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{f.name}</div>
                    <div className="text-[11px] text-slate-500">{f.type} · {f.funder}</div>
                  </div>
                  <div className={`text-xs font-mono font-semibold shrink-0 ${urg}`}>{f.days_until}d</div>
                </Link>
              );
            })}
            {upcoming.length === 0 && <div className="text-xs text-slate-500">No deadlines in the next 90 days.</div>}
          </div>
        </Card>

        <Card className="p-5 border-slate-200" data-testid="stale-evidence-card">
          <div className="flex items-center gap-2 text-slate-900 font-semibold mb-3"><Clock className="w-4 h-4 text-rose-600" /> Stale Evidence (&gt; 90 days)</div>
          <div className="space-y-1">
            {stale.slice(0, 5).map(s => (
              <Link key={s.id} to={`/programs/${s.id}`} className="flex justify-between items-start gap-3 py-2 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 rounded px-1 -mx-1">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate">{s.name}</div>
                  <div className="text-[11px] mt-0.5"><EvidenceBadge level={s.evidence_level} /></div>
                </div>
                <div className="text-xs font-mono font-semibold text-rose-700 shrink-0">{s.age_days}d</div>
              </Link>
            ))}
            {stale.length === 0 && <div className="text-xs text-slate-500">All verified records are current. Nice.</div>}
          </div>
        </Card>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        <Card className="p-5 md:col-span-2 border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-slate-900">Evidence Health</h3>
            <Link to="/programs" className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-slate-900 font-semibold">Programs by evidence level →</Link>
          </div>
          <div className="space-y-3">
            {["verified_fact","partner_confirmed","public_source","estimate","assumption","needs_verification"].map(lvl => {
              const total = k.programs || 1;
              const val = evBreak[lvl] || 0;
              const pct = Math.round((val / total) * 100);
              return (
                <div key={lvl} className="flex items-center gap-3">
                  <div className="w-40 shrink-0"><EvidenceBadge level={lvl} /></div>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-slate-900" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="w-16 text-right font-mono text-sm text-slate-700">{val}</div>
                </div>
              );
            })}
          </div>
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="flex items-center gap-2 text-slate-900 font-semibold mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-600" /> Attention
          </div>
          <div className="space-y-3 text-sm">
            <Link to="/programs" className="flex justify-between border-b border-slate-100 pb-2 hover:text-[#C25E00]">
              <span>Programs needing verification</span>
              <span className="font-mono font-bold">{k.needs_verification || 0}</span>
            </Link>
            <Link to="/programs" className="flex justify-between border-b border-slate-100 pb-2 hover:text-emerald-800">
              <span>Verified programs</span>
              <span className="font-mono font-bold text-emerald-700">{k.verified_programs || 0}</span>
            </Link>
            <div className="text-[11px] text-slate-500 leading-relaxed pt-2">
              Every intelligence item carries a source and evidence level. Prioritize converting <em>Assumptions</em> and <em>Needs Verification</em> into <em>Partner Confirmed</em> facts.
            </div>
          </div>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Card className="p-5 border-slate-200">
          <div className="flex items-center gap-2 mb-3 text-slate-900 font-semibold"><TrendingUp className="w-4 h-4" /> Programs by Vertical</div>
          <div className="space-y-2">
            {Object.entries(byV).sort((a, b) => b[1] - a[1]).map(([v, c]) => (
              <Link to={`/programs`} key={v} className="flex items-center justify-between text-sm hover:text-[#C25E00]">
                <span className="capitalize">{v.replace("_", " ")}</span>
                <span className="font-mono">{c}</span>
              </Link>
            ))}
          </div>
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="flex items-center gap-2 mb-3 text-slate-900 font-semibold"><MapPin className="w-4 h-4" /> Properties by Metro</div>
          <div className="space-y-4">
            {[
              { metro: "San Antonio", counties: ["Bexar","Comal","Guadalupe","Kendall"] },
              { metro: "Austin", counties: ["Travis","Williamson","Hays","Bastrop","Caldwell"] },
              { metro: "Houston", counties: ["Harris","Fort Bend","Montgomery","Brazoria","Galveston"] },
              { metro: "Dallas–Fort Worth", counties: ["Dallas","Tarrant","Collin","Denton","Rockwall"] },
            ].map(({ metro, counties }) => {
              const total = counties.reduce((s, c) => s + (byC[c] || 0), 0);
              return (
                <Link to="/properties" key={metro} className="block hover:bg-slate-50 rounded px-1 -mx-1">
                  <div className="flex items-center justify-between text-sm font-semibold text-slate-900">
                    <span>{metro}</span>
                    <span className="font-mono">{total}</span>
                  </div>
                  <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-slate-600">
                    {counties.map(c => (
                      <div key={c} className="flex justify-between"><span>{c}</span><span className="font-mono">{byC[c] || 0}</span></div>
                    ))}
                  </div>
                </Link>
              );
            })}
          </div>
        </Card>
      </div>

      {demand && demand.total > 0 && (
        <Card className="p-5 border-slate-200 bg-white" data-testid="demand-density-panel">
          <div className="flex items-center gap-2 text-slate-900 font-semibold mb-3"><Radio className="w-4 h-4 text-[#C25E00]" /> Anonymous demand signal · last 8 weeks</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Total signals</div><div className="text-2xl font-mono font-bold">{demand.total}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Took handoff</div><div className="text-2xl font-mono font-bold text-emerald-700">{demand.connected}</div></div>
            <div className="col-span-2"><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">By situation</div>
              <div className="flex flex-wrap gap-1">{Object.entries(demand.by_situation).map(([k,n]) => <span key={k} className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">{k}: {n}</span>)}</div>
            </div>
          </div>
          <div className="text-[10px] text-slate-500 italic mt-2">One anonymous row per search · situation × area × week · no identity captured.</div>
        </Card>
      )}
    </div>
  );
}
